import "./style.scss";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { Fragment, memo, useCallback, useEffect } from "react";
import ConditionSelectors from "@builder/components/general/ConditionSelectors";
import useConditions, { ConditionState } from "@builder/hooks/useConditions";
import { convertConditionsToString, extractSources, parseConditionString } from "@builder/utils/conditionsHelpers";
import { ConditionsDataProvider } from "@builder/context/ConditionsData";
import OliverText from "@builder/components/general/OliverText";
import Button from "@builder/components/general/Button";
import { updateStepperActions } from "@builder/store/slices/actionBuilderSlice";
import { ActionBuilderType } from "@builder/utils/actionBuilderHelpers";
import { ActionType } from "@common/build/types";
import { useTranslation } from "react-i18next";

const Condition = memo(({ selectedTab, isTrigger = false }: { selectedTab: string | null; isTrigger?: boolean; isAuto?: boolean }) => {
    const actionStep = useAppSelector(state => state.actionBuilder.actionsEditor.find(item => item.id === selectedTab));
    const dispatch = useAppDispatch();

    const condition = actionStep?.action?.condition;
    let defaultValues: ConditionState | undefined = condition ? parseConditionString(condition) : undefined;
    if (defaultValues?.conditions[0].length === 0) {
        defaultValues = undefined;
    }

    const { t } = useTranslation();

    const { state, handleSelect, addCondition, addGroup, removeCondition } = useConditions(defaultValues);
    // const [error, setError] = useState<Record<number, Record<string, boolean>> | null>(null);

    const handleCondition = useCallback(() => {
        const conditions = convertConditionsToString(state);

        const sources = extractSources(conditions);

        if (actionStep) {
            const updatedAction = { ...actionStep.action, type: ActionType.CONDITION, condition: conditions };
            let updatedTitle = actionStep.title;

            if (actionStep.type !== ActionBuilderType.START) {
                updatedTitle =
                    sources.length > 0 ? `${t("popup.actionBuilder." + Condition)}: ${sources.join(", ")}` : t("popup.actionBuilder.Condition");
            }

            const updatedItem = { ...actionStep, action: updatedAction, title: updatedTitle, noTranslate: true };
            dispatch(updateStepperActions({ id: selectedTab, item: updatedItem }));
        }
    }, [state]);

    useEffect(() => {
        handleCondition();
    }, [state]);

    return (
        <div className="actions-wrapper-layout condition-wrapper">
            <div className="conditions">
                <div className="texts">
                    <div className="premium-texts">
                        <OliverText text={t("popup.actionBuilder.Set your condition trigger")} size="s" weight="medium-heavy" color="base-blue" />
                    </div>
                    <OliverText
                        text={t("popup.actionBuilder.The action will only be initiated if all conditions are met.")}
                        size="xs"
                        weight="medium"
                        color="base-grey"
                        translatePrefix="popup.actionBuilder."
                    />
                </div>
                <div className="conditions-body">
                    {state.conditions.map((condition, groupIndex) => (
                        <Fragment key={groupIndex}>
                            <div className="conditions-selectors-wrapper">
                                <ConditionsDataProvider>
                                    <ConditionSelectors
                                        error={null}
                                        isEdit={defaultValues !== undefined}
                                        conditions={condition}
                                        handleSelect={(index, key, value) => handleSelect(groupIndex, index, key, value)}
                                        removeCondition={index => removeCondition(groupIndex, index)}
                                        addCondition={() => addCondition(groupIndex)}
                                        isTrigger={isTrigger}
                                        groupIndex={groupIndex}
                                    />
                                </ConditionsDataProvider>
                            </div>
                            {condition && condition.length > 0 && groupIndex < state.conditions.length - 1 && (
                                <div className="decision-selectors">
                                    {<OliverText text="Or" size="xs" weight="medium-heavy" translatePrefix="popup.actionBuilder." />}
                                </div>
                            )}
                        </Fragment>
                    ))}
                    <div className="decision-selectors">
                        <Button
                            onClick={addGroup}
                            buttonStyle="secondary"
                            text="Or"
                            size="small"
                            style={{ width: "100%", height: "32px", marginBottom: "8px" }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Condition;
